// =============================================================================
// services/audio.ts — bare-RN voice-message record + playback adapter
// (blueprint §1 swap #11: expo-audio → react-native-audio-recorder-player).
//
// The Expo DM screen used expo-audio's HOOKS — useAudioRecorder /
// useAudioRecorderState (record) and useAudioPlayer / useAudioPlayerStatus
// (play). react-native-audio-recorder-player is a single imperative SINGLETON
// player (startRecorder/stopRecorder, startPlayer/stopPlayer + progress
// listeners), so we wrap it behind two small stable APIs:
//
//   recorder.start() / recorder.stop({cancel}) / recorder.subscribe(cb)
//   playback.play(uri, {onProgress,onEnd}) / playback.pause() / playback.stop()
//
// The DM screen + AudioMessageBubble drive these with plain React state instead
// of Expo's hooks. Output is m4a (matches what the upload/backend expect).
//
// LAZY + GRACEFUL: the native lib is a P3-added dep (declared in package.json;
// the pod is brought in by the batch `pod install`). It's loaded via a guarded
// require so this module — and the DM screen — type-check and run before the pod
// exists: record/play simply no-op (record returns null uri) until then. Same
// pattern as services/fs.ts + services/haptics.ts + the deferred react-native-video.
// =============================================================================

let _mod: any = null;
let _tried = false;

// The lib exports a default singleton instance in v3, and a class in v4
// (AudioRecorderPlayer) you `new` once. Normalise both into one instance.
function instance(): any {
  if (_tried) return _mod;
  _tried = true;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const lib = require('react-native-audio-recorder-player');
    const candidate = lib?.default ?? lib;
    if (typeof candidate === 'function') {
      // v4: a class — instantiate the shared singleton.
      _mod = new candidate();
    } else {
      // v3: already a singleton instance.
      _mod = candidate;
    }
  } catch {
    _mod = null;
  }
  return _mod;
}

/** True once the native audio lib is present (pods installed). */
export function isAvailable(): boolean {
  return !!instance();
}

// ---- Recording --------------------------------------------------------------

let _recording = false;

export interface RecorderProgress {
  /** Elapsed recording time in milliseconds. */
  durationMillis: number;
}

export const recorder = {
  isRecording(): boolean {
    return _recording;
  },

  /**
   * Start recording to a temp m4a. Returns true on success, false if the lib is
   * unavailable or recording could not start (caller stays graceful). Subscribe
   * via `subscribe()` for live elapsed time.
   */
  async start(): Promise<boolean> {
    const m = instance();
    if (!m?.startRecorder) return false;
    try {
      await m.startRecorder();
      _recording = true;
      return true;
    } catch {
      _recording = false;
      return false;
    }
  },

  /**
   * Stop recording. Returns the local file uri of the recorded m4a, or null on
   * failure / if `cancel` is set (the caller discards it). Always tears down the
   * progress listener + clears the recording flag.
   */
  async stop(): Promise<string | null> {
    const m = instance();
    _recording = false;
    if (!m?.stopRecorder) return null;
    try {
      const uri = await m.stopRecorder();
      try {
        m.removeRecordBackListener?.();
      } catch {
        /* ignore */
      }
      return typeof uri === 'string' && uri ? uri : null;
    } catch {
      return null;
    }
  },

  /**
   * Subscribe to live recording progress (~every tick). Returns an unsubscribe.
   * No-ops gracefully if the lib is unavailable.
   */
  subscribe(cb: (p: RecorderProgress) => void): () => void {
    const m = instance();
    if (!m?.addRecordBackListener) return () => {};
    try {
      m.addRecordBackListener((e: any) => {
        cb({ durationMillis: Number(e?.currentPosition) || 0 });
      });
    } catch {
      return () => {};
    }
    return () => {
      try {
        m.removeRecordBackListener?.();
      } catch {
        /* ignore */
      }
    };
  },
};

// ---- Playback ---------------------------------------------------------------

export interface PlaybackProgress {
  /** Current playback position in milliseconds. */
  currentMillis: number;
  /** Total duration in milliseconds (0 until known). */
  durationMillis: number;
}

let _playingUri: string | null = null;

export const playback = {
  playingUri(): string | null {
    return _playingUri;
  },

  /**
   * Start playing `uri`. Calls `onProgress` as it advances and `onEnd` when it
   * completes. Returns true on success. Stops any current playback first (the lib
   * is a singleton — one stream at a time, which matches the DM "one bubble plays
   * at a time" rule). No-ops gracefully if the lib is unavailable or uri is blank.
   */
  async play(
    uri: string,
    handlers?: { onProgress?: (p: PlaybackProgress) => void; onEnd?: () => void }
  ): Promise<boolean> {
    const m = instance();
    if (!m?.startPlayer || !uri) return false;
    try {
      await this.stop();
      await m.startPlayer(uri);
      _playingUri = uri;
      m.addPlayBackListener?.((e: any) => {
        const current = Number(e?.currentPosition) || 0;
        const duration = Number(e?.duration) || 0;
        handlers?.onProgress?.({ currentMillis: current, durationMillis: duration });
        if (duration > 0 && current >= duration) {
          handlers?.onEnd?.();
        }
      });
      return true;
    } catch {
      _playingUri = null;
      return false;
    }
  },

  /** Pause playback (resume not separately tracked — the DM UI restarts on tap). */
  async pause(): Promise<void> {
    const m = instance();
    if (!m?.pausePlayer) return;
    try {
      await m.pausePlayer();
    } catch {
      /* ignore */
    }
  },

  /** Stop + release the current stream and clear the playing-uri flag. */
  async stop(): Promise<void> {
    const m = instance();
    _playingUri = null;
    if (!m?.stopPlayer) return;
    try {
      await m.stopPlayer();
      m.removePlayBackListener?.();
    } catch {
      /* ignore */
    }
  },
};

export default { isAvailable, recorder, playback };
